import type { Client } from '@bugsnag/js';
import type { BugsnagErrorBoundary } from '@bugsnag/plugin-react';
import { getRootCause, stringifyError } from '@ui/utils/error';
import React from 'react';
import {
  InsufficientFundsError,
  SocketClosedError,
  UserRejectedRequestError,
} from 'viem';

export let bugsnagClient: Client | undefined;
let bugsnagClientProm: Promise<Client> | null;
let ErrorBoundary: BugsnagErrorBoundary | undefined;
const key = process.env.NEXT_PUBLIC_BUGSNAG_API_KEY;
export async function getBugsnagClient() {
  if (typeof window === 'undefined' || process.env.NODE_ENV === 'development') {
    return;
  }

  if (bugsnagClient) {
    return bugsnagClient;
  }

  if (bugsnagClientProm) {
    return bugsnagClientProm;
  }

  bugsnagClientProm = new Promise(async (resolve, reject) => {
    const instance = (await import('@bugsnag/js')).default;
    const BugsnagPluginReact = (await import('@bugsnag/plugin-react')).default;
    if (!key) {
      reject('process.env.NEXT_PUBLIC_BUGSNAG_API_KEY undefined');

      return;
    }

    bugsnagClient = instance.start({
      apiKey: key,
      appVersion: process.env.APP_VERSION,
      releaseStage: process.env.APP_ENV,
      plugins: [new BugsnagPluginReact()],
    });
    resolve(bugsnagClient);
    bugsnagClientProm = null;
  });

  return bugsnagClientProm;
}

export const getErrorBoundary = () => {
  if (ErrorBoundary) {
    return ErrorBoundary;
  }
  if (bugsnagClient) {
    ErrorBoundary = bugsnagClient
      ?.getPlugin('react')
      ?.createErrorBoundary(React);

    return ErrorBoundary;
  }
};

/**
 * selectively report error to bugsnag and log
 * transaction errors: https://viem.sh/docs/glossary/errors#errors
 */
export const tryReportError = (e: Error, msg?: string, from?: unknown) => {
  const rootCause = getRootCause(e);
  const errorToReport = msg ? new Error(msg, { cause: rootCause }) : e;
  if (e.name === 'AbortError' || rootCause.name === 'AbortError') {
    // omit error report, as it is expected when user switch route or unmount component
    console.warn('AbortError', e);

    return;
  }

  if (from === 'estimateGas') {
    // do not report estimate error, as it usually can fail due to contract restriction
    console.warn('failed estimate gas', e);
  } else if (rootCause instanceof UserRejectedRequestError) {
    // omit error report
    console.error(e);

    return;
  } else if (rootCause instanceof InsufficientFundsError) {
    // omit error report
    console.error(e);

    return;
  } else if (rootCause instanceof SocketClosedError) {
    // filter websocket auto close error, will always auto retry
    console.error(e);

    return;
  } else {
    // TODO Bugsnag
    // bugsnagClient?.notify(errorToReport, (event) => {
    //   event.addMetadata('rootCause', {
    //     additionalInfo: stringifyError(rootCause),
    //   });
    // });
    console.error(errorToReport);
    console.error(stringifyError(rootCause));
  }
};
